import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { toast } from 'sonner';
import { Plus, Save, Trash2, Edit3, Truck } from 'lucide-react';

const empty = () => ({ code: '', name: '', rate: 0, description: '', active: true, order_index: 99, free_over: '' });

function MethodRow({ m, onEdit, onDelete, onToggle }) {
  return (
    <Panel className="p-4" data-testid={`shipping-row-${m.code}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Truck size={16} style={{ color: 'var(--heritage-plum)' }} />
            <span className="h-serif text-lg" style={{ color: 'var(--heritage-plum)' }}>{m.name}</span>
            <span className="h-chip text-[9px]" style={{ background: m.active ? 'rgba(91,110,44,0.18)' : 'rgba(154,44,44,0.15)', borderColor: 'transparent', color: 'var(--heritage-plum)' }}>{m.active ? 'ACTIVE' : 'HIDDEN'}</span>
          </div>
          <div className="text-xs opacity-75 mt-1" style={{ color: 'var(--heritage-plum)' }}>Code: <code>{m.code}</code>{m.description ? ` · ${m.description}` : ''}</div>
        </div>
        <div className="text-right">
          <div className="h-display text-2xl" style={{ color: 'var(--heritage-plum)' }}>${Number(m.rate).toFixed(2)}</div>
          {m.free_over != null && <div className="text-[10px] opacity-75" style={{ color: 'var(--heritage-plum)' }}>Free over ${m.free_over}</div>}
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 mt-3">
        <button onClick={() => onToggle(m)} className="h-btn-outline text-xs">{m.active ? 'Hide' : 'Activate'}</button>
        <button onClick={() => onEdit(m)} className="h-btn-outline text-xs" data-testid={`shipping-edit-${m.code}`}><Edit3 size={12} className="mr-1" /> Edit</button>
        <button onClick={() => onDelete(m)} className="text-xs inline-flex items-center gap-1 opacity-80 hover:opacity-100" style={{ color: '#9A2C2C' }} data-testid={`shipping-delete-${m.code}`}>
          <Trash2 size={12} /> Delete
        </button>
      </div>
    </Panel>
  );
}

function Editor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    ...initial,
    free_over: initial.free_over ?? '',
  });
  const [saving, setSaving] = useState(false);
  const isNew = !form.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.code || !form.name) { toast.error('Code and name are required'); return; }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toLowerCase().replace(/\s+/g, '-'),
        name: form.name,
        rate: Number(form.rate) || 0,
        description: form.description || '',
        active: !!form.active,
        order_index: Number(form.order_index) || 0,
        free_over: form.free_over === '' || form.free_over == null ? null : Number(form.free_over),
      };
      if (isNew) {
        await api.post('/admin/shop/shipping-methods', payload);
        toast.success('Method created');
      } else {
        await api.put(`/admin/shop/shipping-methods/${form.id}`, payload);
        toast.success('Saved');
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const Input = (p) => (
    <input {...p}
      className={`w-full rounded-md border px-3 py-2 text-sm bg-white ${p.className || ''}`}
      style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }} />
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(75,24,56,0.55)' }} onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: '#FBF8F1', border: '3px solid var(--heritage-plum)' }} onClick={(e) => e.stopPropagation()} data-testid="shipping-editor">
        <div className="px-6 py-4 border-b" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
          <div className="h-eyebrow">{isNew ? 'New shipping method' : 'Edit shipping method'}</div>
          <div className="h-display text-2xl mt-0.5" style={{ color: 'var(--heritage-plum)' }}>{form.name || 'Untitled'}</div>
        </div>
        <div className="p-6 space-y-3">
          <label className="block">
            <div className="h-eyebrow mb-1.5">Display name</div>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Wellington Local" data-testid="shipping-name" />
          </label>
          <label className="block">
            <div className="h-eyebrow mb-1.5">Code</div>
            <Input value={form.code} onChange={(e) => set('code', e.target.value)} placeholder="wellington" data-testid="shipping-code" />
          </label>
          <label className="block">
            <div className="h-eyebrow mb-1.5">Rate (NZD)</div>
            <Input type="number" step="0.01" value={form.rate} onChange={(e) => set('rate', e.target.value)} data-testid="shipping-rate" />
          </label>
          <label className="block">
            <div className="h-eyebrow mb-1.5">Description (optional)</div>
            <Input value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="e.g. NZ Post standard, 2–4 working days" />
          </label>
          <label className="block">
            <div className="h-eyebrow mb-1.5">Free over (optional)</div>
            <Input type="number" step="0.01" value={form.free_over} onChange={(e) => set('free_over', e.target.value)} placeholder="e.g. 200 for free over $200" />
          </label>
          <label className="block">
            <div className="h-eyebrow mb-1.5">Sort order</div>
            <Input type="number" value={form.order_index} onChange={(e) => set('order_index', e.target.value)} />
          </label>
          <label className="flex items-center gap-2 pt-1">
            <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} />
            <span className="text-sm" style={{ color: 'var(--heritage-plum)' }}>Active (shown at checkout)</span>
          </label>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
          <button onClick={onClose} className="h-btn-outline text-xs">Cancel</button>
          <button onClick={save} disabled={saving} className="h-btn text-xs" data-testid="shipping-save"><Save size={12} className="mr-1" /> {saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminShopShipping() {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/shop/shipping-methods');
      setMethods(data.methods || []);
    } catch { toast.error('Failed to load shipping'); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.name}"?`)) return;
    try { await api.delete(`/admin/shop/shipping-methods/${m.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const toggle = async (m) => {
    try {
      await api.put(`/admin/shop/shipping-methods/${m.id}`, { ...m, active: !m.active });
      toast.success(!m.active ? 'Activated' : 'Hidden');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-5" data-testid="admin-shop-shipping">
      <div className="h-panel-thin p-6 flex items-center justify-between">
        <div>
          <div className="h-eyebrow">Store</div>
          <div className="h-display text-4xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Shipping Rates</div>
          <p className="text-sm opacity-85 mt-1" style={{ color: 'var(--heritage-plum)' }}>Options shown to shoppers at checkout. Add, edit, hide, or delete rates any time.</p>
        </div>
        <button onClick={() => setEditing(empty())} className="h-btn text-xs" data-testid="shipping-new">
          <Plus size={12} className="mr-1" /> New rate
        </button>
      </div>

      {loading && <div className="h-eyebrow">Loading...</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {methods.map((m) => (
          <MethodRow key={m.id} m={m} onEdit={setEditing} onDelete={remove} onToggle={toggle} />
        ))}
      </div>

      {editing && <Editor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
